import { useState, useEffect, useRef } from 'react'
import type {
  MetadataGenerator,
  Metadata,
  GenerationOptions,
  ValidationResult,
} from '../contracts/metadataGenerator'
import { PLATFORM_CATEGORIES as CATEGORIES } from '../contracts/metadataGenerator'
import type { ImageFile } from '../contracts/uploadService'
import { AppIcons, ButtonIcon, AddIcon, DeleteIcon, SuccessIcon } from './icons'
import type { ImageContext } from '../contracts/metadataGenerator'
import { ExifExtractorService } from '../services/ExifExtractorService'
import { reverseGeocodingService, type LocationInfo } from '../services/ReverseGeocodingService'
import { useCredits } from '../hooks/useCredits'

interface MobileMetadataEditorProps {
  metadataGenerator: MetadataGenerator
  images: ImageFile[]
  existingMetadata: Record<string, Metadata>
  imageContext: ImageContext
  onMetadataUpdate: (imageId: string, metadata: Metadata) => void
  isBatchGenerating: boolean
  batchProgress: { current: number; total: number }
  onBatchGeneratingChange: (generating: boolean) => void
  onBatchProgressChange: (progress: { current: number; total: number }) => void
  onAddMoreImages: (files: FileList) => void
  onDeleteImage: (imageId: string) => void
  onNavigateToExport?: () => void
}

// Get platform-specific keyword limit
const getKeywordLimit = (platform?: string): number => {
  switch (platform) {
    case 'shutterstock': return 50
    case 'adobe-stock': return 49
    case 'getty-images':
    case 'istock': return 50
    case 'generic':
    default: return 50
  }
}

// Get platform-specific field requirements
const getPlatformRequirements = (platform?: string) => {
  switch (platform) {
    case 'shutterstock':
      return {
        showDescription: true,
        showTitle: false,
        descriptionLabel: 'Description',
        descriptionRequired: true,
        maxDescriptionLength: 200,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 0,
        titlePlaceholder: '',
        titleHelp: '',
        showTitleHelp: false,
        descriptionPlaceholder: 'Describe the main subject and visual elements',
        descriptionHelp: 'Clear, concise description focusing on visual elements',
        showDescriptionHelp: true,
        categoryHelp: 'Select the most appropriate category for Shutterstock'
      }
    case 'adobe-stock':
      return {
        showDescription: false,
        showTitle: true,
        descriptionLabel: 'Title',
        descriptionRequired: false,
        maxDescriptionLength: 0,
        maxKeywords: 49,
        titleLabel: 'Title',
        titleRequired: true,
        maxTitleLength: 200,
        titlePlaceholder: 'Enter descriptive title (serves as description)',
        titleHelp: 'This field serves as both title and description for Adobe Stock',
        showTitleHelp: true,
        descriptionPlaceholder: '',
        descriptionHelp: '',
        showDescriptionHelp: false,
        categoryHelp: 'Adobe Stock uses numeric categories - select the closest match'
      }
    case 'istock':
      return {
        showDescription: true,
        showTitle: true,
        descriptionLabel: 'Description',
        descriptionRequired: true,
        maxDescriptionLength: 1500,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 200,
        titlePlaceholder: 'Enter optional title',
        titleHelp: 'Optional title field for iStock',
        showTitleHelp: true,
        descriptionPlaceholder: 'Detailed description of the image content',
        descriptionHelp: 'iStock allows longer descriptions - be detailed and specific',
        showDescriptionHelp: true,
        categoryHelp: 'Choose the category that best fits your image content'
      }
    case 'getty-images':
      return {
        showDescription: true,
        showTitle: false,
        descriptionLabel: 'Description',
        descriptionRequired: true,
        maxDescriptionLength: 200,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 0,
        titlePlaceholder: '',
        titleHelp: '',
        showTitleHelp: false,
        descriptionPlaceholder: 'Describe the image content and context',
        descriptionHelp: 'Focus on newsworthy and editorial aspects for Getty Images',
        showDescriptionHelp: true,
        categoryHelp: 'Select category appropriate for Getty Images editorial focus'
      }
    default:
      return {
        showDescription: true,
        showTitle: true,
        descriptionLabel: 'Description',
        descriptionRequired: false,
        maxDescriptionLength: 200,
        maxKeywords: 50,
        titleLabel: 'Title',
        titleRequired: false,
        maxTitleLength: 200,
        titlePlaceholder: 'Enter image title',
        titleHelp: 'Optional title for the image',
        showTitleHelp: true,
        descriptionPlaceholder: 'Describe the image content',
        descriptionHelp: 'Describe what is shown in the image',
        showDescriptionHelp: true,
        categoryHelp: 'Select the most appropriate category'
      }
  }
}

export function MobileMetadataEditor({
  metadataGenerator,
  images,
  existingMetadata,
  imageContext,
  onMetadataUpdate,
  isBatchGenerating,
  batchProgress,
  onBatchGeneratingChange,
  onBatchProgressChange,
  onAddMoreImages,
  onDeleteImage,
  onNavigateToExport
}: MobileMetadataEditorProps) {
  const [activeTab, setActiveTab] = useState<'gallery' | 'edit'>('gallery')
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [metadata, setMetadata] = useState<Metadata | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [generationOptions, setGenerationOptions] = useState<GenerationOptions>({
    keywordLimit: getKeywordLimit(imageContext.platform),
    style: 'commercial',
    includeCompliance: true,
    confidenceThreshold: 0.7,
  })
  const [error, setError] = useState<string | null>(null)
  const [newKeyword, setNewKeyword] = useState('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showExportCTA, setShowExportCTA] = useState(false)
  const [isExifCollapsed, setIsExifCollapsed] = useState(true)
  const [currentLocationInfo, setCurrentLocationInfo] = useState<LocationInfo | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const exifExtractor = new ExifExtractorService()

  // Credit management
  const {
    hasCredits,
    hasSufficientCredits,
    checkCanProcess,
    deductForImageProcessing,
    deductForBatchProcessing,
    isLoading: creditsLoading,
    error: creditsError
  } = useCredits()

  // Touch/swipe handling
  const [touchStart, setTouchStart] = useState<number>(0)
  const [touchEnd, setTouchEnd] = useState<number>(0)

  // Get current selected image
  const selectedImage = images[selectedImageIndex] || null

  // Get platform-specific categories
  const platformCategories = imageContext.platform 
    ? CATEGORIES[imageContext.platform] 
    : CATEGORIES['generic']
  const availableCategories = [...platformCategories]
  
  // Get platform-specific field requirements
  const platformRequirements = getPlatformRequirements(imageContext.platform)

  // Load existing metadata when selecting an image
  useEffect(() => {
    if (selectedImage && existingMetadata[selectedImage.id]) {
      setMetadata(existingMetadata[selectedImage.id])
    } else {
      setMetadata(null)
    }
  }, [selectedImage, existingMetadata])

  // Load location info for EXIF display when image has GPS data
  useEffect(() => {
    if (selectedImage?.exifData?.location?.latitude && selectedImage?.exifData?.location?.longitude) {
      reverseGeocodingService.getLocationInfo(
        selectedImage.exifData.location.latitude,
        selectedImage.exifData.location.longitude
      )
      .then(locationInfo => {
        setCurrentLocationInfo(locationInfo)
      })
      .catch(error => {
        console.warn('Failed to load location info for display:', error)
        setCurrentLocationInfo(null)
      })
    } else {
      setCurrentLocationInfo(null)
    }
  }, [selectedImage])

  useEffect(() => {
    if (metadata) {
      const validationResult = metadataGenerator.validateMetadata(metadata, imageContext.platform)
      setValidation(validationResult)
    }
  }, [metadata, metadataGenerator, imageContext.platform])

  // Update keyword limit when platform changes
  useEffect(() => {
    setGenerationOptions(prev => ({
      ...prev,
      keywordLimit: getKeywordLimit(imageContext.platform)
    }))
  }, [imageContext.platform])

  // Helper function to enhance context with EXIF data (date and location) for editorial images
  const enhanceContextWithExifData = async (image: ImageFile, context: ImageContext): Promise<ImageContext> => {
    let enhancedContext = { ...context }

    // Add EXIF date if not already set
    if (!enhancedContext.date && image.exifData?.dateTime?.dateTimeOriginal) {
      enhancedContext.date = image.exifData.dateTime.dateTimeOriginal
    }

    // Add EXIF location if not already set and GPS data is available
    if (!enhancedContext.location && image.exifData?.location?.latitude && image.exifData?.location?.longitude) {
      try {
        const locationInfo = await reverseGeocodingService.getLocationInfo(
          image.exifData.location.latitude,
          image.exifData.location.longitude
        )
        if (locationInfo?.formattedAddress) {
          enhancedContext.location = locationInfo.formattedAddress
        }
      } catch (error) {
        console.warn('Failed to get location info:', error)
        // Continue without location enhancement
      }
    }

    return enhancedContext
  }

  const handleGenerateMetadata = async () => {
    if (!selectedImage) return

    // Check if user has credits before processing
    if (!checkCanProcess(1)) {
      setError('Insufficient credits. Please upgrade your plan to continue processing images.')
      return
    }

    try {
      setIsGenerating(true)
      setError(null)

      // Deduct credit for image processing before AI generation
      await deductForImageProcessing(selectedImage.id, 'ai_metadata_generation')

      // Enhance context with EXIF data if available
      const enhancedContext = await enhanceContextWithExifData(selectedImage, imageContext)

      const optionsWithMaxKeywords = {
        ...generationOptions,
        keywordLimit: getKeywordLimit(imageContext.platform),
        context: enhancedContext
      }

      const generatedMetadata = await metadataGenerator.generateMetadata(
        selectedImage,
        optionsWithMaxKeywords,
      )

      setMetadata(generatedMetadata)
      onMetadataUpdate(selectedImage.id, generatedMetadata)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate metadata'

      // Check if it's a credit-related error
      if (errorMessage.includes('insufficient') || errorMessage.includes('credit')) {
        setError('Insufficient credits. Please upgrade your plan to continue processing images.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleBatchGenerate = async () => {
    // Only generate metadata for images that don't have it yet
    const imagesToProcess = images.filter(image => !existingMetadata[image.id])

    if (imagesToProcess.length === 0) {
      setSuccessMessage('All images already have metadata!')
      setTimeout(() => setSuccessMessage(null), 3000)
      return
    }

    // Check if user has sufficient credits for batch processing
    if (!checkCanProcess(imagesToProcess.length)) {
      setError(`Insufficient credits for batch processing ${imagesToProcess.length} images. Please upgrade your plan or process fewer images.`)
      return
    }

    try {
      onBatchGeneratingChange(true)
      setError(null)
      setSuccessMessage(null)
      setShowExportCTA(false)
      onBatchProgressChange({ current: 0, total: imagesToProcess.length })

      // Generate unique batch ID for credit deduction
      const batchId = `batch_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`

      // Deduct credits for entire batch upfront to prevent partial processing
      await deductForBatchProcessing(batchId, imagesToProcess.length, 'bulk_metadata_generation')

      const results: Record<string, Metadata> = {}
      let successCount = 0

      for (let i = 0; i < imagesToProcess.length; i++) {
        const image = imagesToProcess[i]

        try {
          // Enhance context with EXIF data if available
          const enhancedContext = await enhanceContextWithExifData(image, imageContext)

          const optionsWithMaxKeywords = {
            ...generationOptions,
            keywordLimit: getKeywordLimit(imageContext.platform),
            context: enhancedContext
          }
          const generatedMetadata = await metadataGenerator.generateMetadata(image, optionsWithMaxKeywords)
          results[image.id] = generatedMetadata
          onMetadataUpdate(image.id, generatedMetadata)
          successCount++

          onBatchProgressChange({ current: successCount, total: imagesToProcess.length })

          // Update display if this is the currently selected image
          if (selectedImage?.id === image.id) {
            setMetadata(generatedMetadata)
          }

          if (i < imagesToProcess.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        } catch (imageError) {
          console.error(`Failed to generate metadata for ${image.name}:`, imageError)
          // Continue with other images - progress is only updated on success
        }
      }
      
      // Show success message and export CTA
      if (successCount > 0) {
        const allImagesHaveMetadata = images.every(image => existingMetadata[image.id])
        const metadataCount = Object.keys(existingMetadata).length
        const hasSignificantProgress = metadataCount >= Math.max(1, Math.floor(images.length * 0.5)) // At least 50% or 1 image
        
        if (allImagesHaveMetadata) {
          setSuccessMessage(`🎉 All ${images.length} images now have metadata! Ready to export.`)
          setShowExportCTA(true)
        } else if (hasSignificantProgress && successCount === imagesToProcess.length) {
          // Just finished processing all remaining images and have significant metadata
          setSuccessMessage(`✅ Generated metadata for ${successCount} image${successCount > 1 ? 's' : ''}! ${metadataCount}/${images.length} images ready.`)
          setShowExportCTA(true)
        } else {
          setSuccessMessage(`✅ Generated metadata for ${successCount} image${successCount > 1 ? 's' : ''}!`)
        }
        
        // Hide success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000)
      }
      
      onBatchProgressChange({ current: successCount, total: imagesToProcess.length })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate batch metadata')
    } finally {
      onBatchGeneratingChange(false)
      onBatchProgressChange({ current: 0, total: 0 })
    }
  }

  const handleMetadataChange = (field: keyof Metadata, value: any) => {
    if (!metadata || !selectedImage) return

    const updatedMetadata = { ...metadata, [field]: value, isEdited: true, editedAt: new Date() }
    
    setMetadata(updatedMetadata)
    onMetadataUpdate(selectedImage.id, updatedMetadata)
  }

  const handleKeywordAdd = () => {
    if (!metadata || !newKeyword.trim()) return
    const trimmedKeyword = newKeyword.trim()
    if (metadata.keywords.includes(trimmedKeyword)) return
    const updatedKeywords = [...metadata.keywords, trimmedKeyword]
    handleMetadataChange('keywords', updatedKeywords)
    setNewKeyword('')
  }

  const handleKeywordRemove = (index: number) => {
    if (!metadata) return
    const updatedKeywords = metadata.keywords.filter((_, i) => i !== index)
    handleMetadataChange('keywords', updatedKeywords)
  }

  const handleAddMoreImagesClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      onAddMoreImages(files)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const navigateToImage = (index: number) => {
    if (index >= 0 && index < images.length) {
      setSelectedImageIndex(index)
    }
  }

  const handleSwipeNext = () => {
    if (selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1)
    }
  }

  const handleSwipePrev = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1)
    }
  }

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(0) // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    // Left swipe = next image, Right swipe = previous image
    if (isLeftSwipe && selectedImageIndex < images.length - 1) {
      handleSwipeNext()
    }
    if (isRightSwipe && selectedImageIndex > 0) {
      handleSwipePrev()
    }
  }

  if (images.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center">
        <div>
          <p className="text-gray-500 text-lg mb-2">No images to edit</p>
          <p className="text-sm text-gray-400">Upload some images to get started</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Mobile Tab Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0 rounded-t-xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">Metadata Editor</h2>
          {imageContext.platform && (
            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
              {imageContext.platform.replace('-', ' ')}
            </span>
          )}
        </div>
        
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'gallery'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Images ({images.length})
          </button>
          <button
            onClick={() => setActiveTab('edit')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'edit'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            disabled={!selectedImage}
          >
            Edit Metadata
          </button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        multiple
        className="hidden"
      />

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'gallery' ? (
          <div className="h-full flex flex-col">
            {/* Current Image Preview */}
            <div className="flex-shrink-0 bg-gray-100 relative">
              {selectedImage ? (
                <div 
                  className="relative"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <img
                    src={selectedImage.preview}
                    alt={selectedImage.name}
                    className="w-full h-64 object-contain bg-gray-50"
                  />
                  
                  {/* Navigation Arrows */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={handleSwipePrev}
                        disabled={selectedImageIndex === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed min-h-12 min-w-12"
                      >
                        <AppIcons.ArrowLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={handleSwipeNext}
                        disabled={selectedImageIndex === images.length - 1}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full disabled:opacity-30 disabled:cursor-not-allowed min-h-12 min-w-12"
                      >
                        <AppIcons.ArrowLeft className="w-6 h-6 rotate-180" />
                      </button>
                    </>
                  )}
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} of {images.length}
                  </div>
                  
                  {/* Status Indicator */}
                  {existingMetadata[selectedImage.id] && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white p-2 rounded-full">
                      <SuccessIcon className="w-5 h-5" />
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Image Details */}
            {selectedImage && (
              <div className="flex-shrink-0 px-4 py-3 bg-white border-b">
                <h3 className="text-lg font-medium text-gray-900 truncate">{selectedImage.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedImage.dimensions?.width || 0}×{selectedImage.dimensions?.height || 0}
                  {' • '}{Math.round(selectedImage.size / 1024)} KB
                  {selectedImage.dimensions?.duration && (
                    <> • {Math.round(selectedImage.dimensions.duration)}s</>
                  )}
                </p>
                {existingMetadata[selectedImage.id] ? (
                  <div className="flex items-center mt-2">
                    <SuccessIcon className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-sm text-green-600 font-medium">Metadata Complete</span>
                  </div>
                ) : (
                  <div className="flex items-center mt-2">
                    <div className="w-4 h-4 rounded-full bg-gray-300 mr-2"></div>
                    <span className="text-sm text-gray-500">No metadata yet</span>
                  </div>
                )}
              </div>
            )}

            {/* Image Grid */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="grid grid-cols-3 gap-3">
                {images.map((image, index) => {
                  const hasMetadata = existingMetadata[image.id] !== undefined
                  const isSelected = selectedImageIndex === index
                  const isProcessing = isBatchGenerating && batchProgress.current === index

                  return (
                    <div
                      key={image.id}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                        isSelected 
                          ? 'border-blue-500 shadow-lg' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img
                        src={image.preview}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Status Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent">
                        {hasMetadata && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <SuccessIcon className="w-4 h-4 text-white" />
                          </div>
                        )}
                        {isProcessing && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full animate-pulse">
                            <div className="w-full h-full bg-blue-400 rounded-full animate-ping"></div>
                          </div>
                        )}
                        
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onDeleteImage(image.id)
                            // Adjust selected index if needed
                            if (selectedImageIndex >= images.length - 1 && selectedImageIndex > 0) {
                              setSelectedImageIndex(selectedImageIndex - 1)
                            }
                          }}
                          className="absolute top-2 left-2 bg-red-500 text-white rounded-full opacity-80 hover:opacity-100 w-8 h-8 flex items-center justify-center"
                        >
                          <AppIcons.X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Image Index */}
                      <div className="absolute bottom-2 left-2 text-white text-xs font-medium bg-black/60 px-2 py-1 rounded">
                        {index + 1}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Progress Bar */}
            {isBatchGenerating && batchProgress.total > 0 && (
              <div className="flex-shrink-0 bg-blue-50 border-t p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">
                    Generating... ({batchProgress.current}/{batchProgress.total})
                  </span>
                  <span className="text-sm text-blue-700 font-medium">
                    {Math.round((batchProgress.current / batchProgress.total) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex-shrink-0 p-4 bg-white border-t space-y-3">
              <div className="space-y-3">
                <button
                  onClick={handleBatchGenerate}
                  disabled={
                    isBatchGenerating ||
                    images.filter(image => !existingMetadata[image.id]).length === 0 ||
                    !checkCanProcess(images.filter(image => !existingMetadata[image.id]).length)
                  }
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium h-12"
                  title={
                    !checkCanProcess(images.filter(image => !existingMetadata[image.id]).length)
                      ? 'Insufficient credits for batch processing'
                      : undefined
                  }
                >
                  {isBatchGenerating
                    ? 'Generating...'
                    : images.filter(image => !existingMetadata[image.id]).length === 0
                    ? 'All Complete'
                    : `Generate Missing (${images.filter(image => !existingMetadata[image.id]).length})`
                  }
                </button>
                
                <button
                  onClick={handleAddMoreImagesClick}
                  className="w-full border border-blue-500 text-blue-600 py-3 px-4 rounded-xl hover:bg-blue-50 transition-colors font-medium flex items-center justify-center space-x-2 h-12"
                >
                  <AddIcon className="w-5 h-5" />
                  <span>Add Images</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Sticky Header with Current Image */}
            {selectedImage && (
              <div className="flex-shrink-0 bg-white border-b px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <img
                      src={selectedImage.preview}
                      alt={selectedImage.name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {selectedImage.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        Image {selectedImageIndex + 1} of {images.length}
                      </p>
                    </div>
                  </div>
                  
                  {/* Navigation Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSwipePrev}
                      disabled={selectedImageIndex === 0}
                      className="p-2 rounded-lg border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 min-h-10 min-w-10"
                    >
                      <AppIcons.ArrowLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSwipeNext}
                      disabled={selectedImageIndex === images.length - 1}
                      className="p-2 rounded-lg border disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 min-h-10 min-w-10"
                    >
                      <AppIcons.ArrowLeft className="w-4 h-4 rotate-180" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="flex-shrink-0 mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="flex-shrink-0 mx-4 mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-green-600 text-sm font-medium">{successMessage}</p>
              </div>
            )}

            {/* Export CTA */}
            {showExportCTA && (
              <div className="flex-shrink-0 mx-4 mt-4 p-5 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl">
                <div className="text-center">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mb-3">
                      <AppIcons.Download className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      🎉 Ready to export!
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Your AI-generated metadata is ready for export
                    </p>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setShowExportCTA(false)
                        onNavigateToExport?.()
                      }}
                      className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-6 rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 font-semibold flex items-center justify-center space-x-2 h-12"
                    >
                      <AppIcons.Download className="w-5 h-5" />
                      <span>Export Now</span>
                    </button>
                    <button
                      onClick={() => setShowExportCTA(false)}
                      className="w-full text-gray-600 hover:text-gray-800 py-2 px-4 rounded-xl hover:bg-white/50 transition-colors text-sm"
                    >
                      Continue editing
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Metadata Form */}
            {selectedImage ? (
              <div className="flex-1 overflow-y-auto bg-white">
                {metadata ? (
                  <div className="space-y-6 py-4">

                    {/* Title Section */}
                    {platformRequirements.showTitle && (
                      <div className="mx-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {platformRequirements.titleLabel}
                          {platformRequirements.titleRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          type="text"
                          value={metadata.title || ''}
                          onChange={(e) => handleMetadataChange('title', e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base h-12"
                          placeholder={platformRequirements.titlePlaceholder}
                          maxLength={platformRequirements.maxTitleLength}
                        />
                        {platformRequirements.showTitleHelp && (
                          <p className="text-xs text-gray-500 mt-2">
                            {platformRequirements.titleHelp} (Max {platformRequirements.maxTitleLength} characters)
                          </p>
                        )}
                        {metadata.title && metadata.title.length > (platformRequirements.maxTitleLength * 0.9) && (
                          <p className="text-xs text-orange-600 mt-1">
                            {metadata.title.length}/{platformRequirements.maxTitleLength} characters
                          </p>
                        )}
                      </div>
                    )}

                    {/* Description Section */}
                    {platformRequirements.showDescription && (
                      <div className="mx-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {platformRequirements.descriptionLabel}
                          {platformRequirements.descriptionRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <textarea
                          value={metadata.description || ''}
                          onChange={(e) => handleMetadataChange('description', e.target.value)}
                          rows={4}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base resize-none h-32"
                          placeholder={platformRequirements.descriptionPlaceholder}
                          maxLength={platformRequirements.maxDescriptionLength}
                        />
                        {platformRequirements.showDescriptionHelp && (
                          <p className="text-xs text-gray-500 mt-2">
                            {platformRequirements.descriptionHelp} (Max {platformRequirements.maxDescriptionLength} characters)
                          </p>
                        )}
                        {metadata.description && metadata.description.length > (platformRequirements.maxDescriptionLength * 0.9) && (
                          <p className="text-xs text-orange-600 mt-1">
                            {metadata.description.length}/{platformRequirements.maxDescriptionLength} characters
                          </p>
                        )}
                      </div>
                    )}

                    {/* Category Section */}
                    <div className="mx-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={metadata.category}
                        onChange={(e) => handleMetadataChange('category', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base h-12"
                      >
                        {availableCategories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                      {platformRequirements.categoryHelp && (
                        <p className="text-xs text-gray-500 mt-2">
                          {platformRequirements.categoryHelp}
                        </p>
                      )}
                    </div>

                    {/* Keywords Section */}
                    <div className="mx-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Keywords ({metadata.keywords.length}/{platformRequirements.maxKeywords})
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      
                      {/* Add keyword input */}
                      <div className="flex space-x-2 mb-4">
                        <input
                          type="text"
                          value={newKeyword}
                          onChange={(e) => setNewKeyword(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleKeywordAdd()
                            }
                          }}
                          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base h-12"
                          placeholder="Enter a new keyword..."
                        />
                        <button
                          onClick={handleKeywordAdd}
                          disabled={!newKeyword.trim()}
                          className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium h-12 min-w-20"
                        >
                          Add
                        </button>
                      </div>
                      
                      {/* Keywords display */}
                      <div className="flex flex-wrap gap-2 min-h-[80px] max-h-60 overflow-y-auto p-3 bg-white border border-gray-300 rounded-lg">
                        {metadata.keywords.map((keyword, index) => (
                          <div
                            key={index}
                            className="inline-flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-3 py-2 rounded-full border border-blue-200 hover:bg-blue-200 transition-colors"
                          >
                            <span className="select-none">{keyword}</span>
                            <button
                              onClick={() => handleKeywordRemove(index)}
                              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              className="ml-2 text-blue-600 active:text-white active:bg-blue-700 text-xs w-6 h-6 flex items-center justify-center rounded-full transition-all duration-150 min-h-6 min-w-6 touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              title="Remove keyword"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        
                        {metadata.keywords.length === 0 && (
                          <span className="text-gray-400 text-sm italic self-center">
                            No keywords added yet. Enter a keyword above and click "Add".
                          </span>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        Max {platformRequirements.maxKeywords} keywords for {imageContext.platform?.replace('-', ' ') || 'this platform'}.
                        {metadata.keywords.length > platformRequirements.maxKeywords && (
                          <span className="text-red-600 ml-1 font-medium">
                            ({metadata.keywords.length - platformRequirements.maxKeywords} over limit)
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Commercial Use & Releases Section */}
                    <div className="mx-4 space-y-3">
                      <h4 className="text-sm font-medium text-gray-700">Usage & Rights</h4>
                      
                      <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:bg-white transition-colors h-12">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={metadata.isCommercial}
                            onChange={(e) => handleMetadataChange('isCommercial', e.target.checked)}
                            className="mr-3 w-5 h-5"
                          />
                          <span className="text-base font-medium">Commercial Use</span>
                        </div>
                        {!metadata.isCommercial && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                            Editorial
                          </span>
                        )}
                      </label>

                      <div className="grid grid-cols-1 gap-3">
                        <label className="flex items-center cursor-pointer p-3 rounded-lg hover:bg-white transition-colors h-12">
                          <input
                            type="checkbox"
                            checked={metadata.hasModelRelease}
                            onChange={(e) => handleMetadataChange('hasModelRelease', e.target.checked)}
                            className="mr-3 w-5 h-5"
                          />
                          <span className="text-base font-medium">Model Release</span>
                        </label>
                        <label className="flex items-center cursor-pointer p-3 rounded-lg hover:bg-white transition-colors h-12">
                          <input
                            type="checkbox"
                            checked={metadata.hasPropertyRelease}
                            onChange={(e) => handleMetadataChange('hasPropertyRelease', e.target.checked)}
                            className="mr-3 w-5 h-5"
                          />
                          <span className="text-base font-medium">Property Release</span>
                        </label>
                      </div>
                    </div>

                    {/* Validation Results */}
                    {validation && !validation.isValid && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-medium text-red-800 mb-2 flex items-center">
                          <AppIcons.AlertTriangle className="w-5 h-5 mr-2" />
                          Validation Errors
                        </h4>
                        {validation.errors.map((error, index) => (
                          <p key={index} className="text-sm text-red-600">
                            • {error.message}
                          </p>
                        ))}
                      </div>
                    )}

                    {validation && validation.warnings.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                          <AppIcons.AlertTriangle className="w-5 h-5 mr-2" />
                          Warnings
                        </h4>
                        {validation.warnings.map((warning, index) => (
                          <div key={index} className="text-sm text-yellow-600">
                            <p>• {warning.message}</p>
                            {warning.suggestion && (
                              <p className="ml-4 italic flex items-center mt-1">
                                <AppIcons.Info className="w-4 h-4 mr-1 text-blue-500" />
                                {warning.suggestion}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Metadata Info */}
                    <div className="text-xs text-gray-500 py-4 border bg-gray-50 rounded-lg px-4 mx-4">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Confidence:</span>
                          <span>{Math.round(metadata.confidence * 100)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Generated:</span>
                          <span>{metadata.generatedAt.toLocaleString()}</span>
                        </div>
                        {metadata.isEdited && metadata.editedAt && (
                          <div className="flex justify-between">
                            <span>Edited:</span>
                            <span>{metadata.editedAt.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* EXIF Data Section */}
                    {selectedImage.exifData && (
                      <div className="mx-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                              <span>📷</span>
                              <span>EXIF Data</span>
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {!imageContext.date && selectedImage.exifData.dateTime?.dateTimeOriginal && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                  Date for metadata
                                </span>
                              )}
                              {!imageContext.location && selectedImage.exifData.location?.latitude && selectedImage.exifData.location?.longitude && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  📍 Location for metadata
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => setIsExifCollapsed(!isExifCollapsed)}
                            className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <span>{isExifCollapsed ? 'Show' : 'Hide'}</span>
                            <AppIcons.ChevronDown className={`w-4 h-4 transform transition-transform ${isExifCollapsed ? '' : 'rotate-180'}`} />
                          </button>
                        </div>
                        
                        {/* Prominent Location Display */}
                        {currentLocationInfo && (
                          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <span className="text-green-600 text-lg">📍</span>
                              </div>
                              <div className="flex-1">
                                <h5 className="text-sm font-medium text-green-800 mb-1">Location Data Available</h5>
                                <p className="text-sm text-green-700 mb-2">{currentLocationInfo.formattedAddress}</p>
                                <p className="text-xs text-green-600">
                                  GPS: {currentLocationInfo.coordinates.latitude.toFixed(6)}, {currentLocationInfo.coordinates.longitude.toFixed(6)}
                                </p>
                                {!imageContext.location && (
                                  <p className="text-xs text-green-600 mt-2 italic">
                                    ✨ This location will be automatically added to editorial metadata
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {!isExifCollapsed && (
                          <div className="grid grid-cols-1 gap-2 text-sm">
                            {Object.entries(exifExtractor.formatForDisplay(selectedImage.exifData)).map(([key, value]) => (
                              <div key={key} className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                                <span className="font-medium text-gray-600">{key}:</span>
                                <span className="text-gray-900 text-right">{value}</span>
                              </div>
                            ))}
                            {/* Add location name if available and not already shown in coordinates */}
                            {currentLocationInfo?.formattedAddress && !exifExtractor.formatForDisplay(selectedImage.exifData)['Location'] && (
                              <div className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                                <span className="font-medium text-gray-600">Location:</span>
                                <span className="text-gray-900 text-right">{currentLocationInfo.formattedAddress}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {!isExifCollapsed && selectedImage.exifData.hasErrors && selectedImage.exifData.errors && selectedImage.exifData.errors.length > 0 && (
                          <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                            <p className="text-xs text-orange-700">
                              <strong>EXIF Warnings:</strong> {selectedImage.exifData.errors.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Regenerate Metadata Button */}
                    <div className="mx-4">
                      <button
                        onClick={handleGenerateMetadata}
                        disabled={isGenerating || !checkCanProcess(1)}
                        className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium h-12 flex items-center justify-center space-x-2"
                        title={
                          !checkCanProcess(1)
                            ? 'Insufficient credits for image processing'
                            : undefined
                        }
                      >
                        {isGenerating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                            <span>Regenerating...</span>
                          </>
                        ) : (
                          <>
                            <AppIcons.RefreshCw className="w-4 h-4" />
                            <span>Regenerate Metadata</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  // No metadata generated yet
                  <div className="flex-1 flex flex-col items-center justify-center p-6 text-center bg-white">
                    <div className="mb-6">
                      <p className="text-lg font-medium text-gray-900 mb-2">No metadata generated yet</p>
                      <p className="text-sm text-gray-500">Generate AI-powered metadata for this image</p>
                    </div>
                    
                    <button
                      onClick={handleGenerateMetadata}
                      disabled={isGenerating || !checkCanProcess(1)}
                      className="bg-blue-600 text-white py-4 px-8 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-lg h-14"
                      title={
                        !checkCanProcess(1)
                          ? 'Insufficient credits for image processing'
                          : undefined
                      }
                    >
                      {isGenerating ? (
                        <div className="flex items-center space-x-3">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Generating...</span>
                        </div>
                      ) : (
                        'Generate Metadata'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6 text-center bg-white">
                <div>
                  <p className="text-lg font-medium text-gray-900 mb-2">No image selected</p>
                  <p className="text-sm text-gray-500 mb-4">Select an image from the Gallery tab to edit metadata</p>
                  <button
                    onClick={() => setActiveTab('gallery')}
                    className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium h-12"
                  >
                    Go to Gallery
                  </button>
                </div>
              </div>
            )}

            {/* Bottom Action Bar */}
            {selectedImage && (
              <div className="flex-shrink-0 p-4 bg-white border-t">
                <button
                  onClick={() => setActiveTab('gallery')}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium h-12"
                >
                  ← Back to Gallery
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}